import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { getTranslation } from "../utils/i18n";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTruckMoving,
  faFileInvoiceDollar,
  faBoxes,
  faPlaneArrival,
  faFileInvoice,
  faTimes,
  faFileCsv,
  faExpandAlt,
  faClockFour,
  faCartFlatbed,
  faListCheck,
  faFileCircleCheck,
} from "@fortawesome/free-solid-svg-icons";
import "./TrafficDetails.css";
import Timeline from "../utils/Timeline";
import { Pie } from "react-chartjs-2";
import logo from "../buckland.png";
import MiniDashboardTrafficDetail from "./MiniDashboardTrafficDetail";
import MiniDashboardDigitalArchive from "./MiniDashboardDigitalArchive";
//import DigitalArchiveModal from "./DigitalArchiveModal";
import DigitalFilesModal from "./DigitalFilesModal";

const TrafficDetails = () => {
  const location = useLocation();
  const [item, setItem] = useState(location.state?.item || {});

  const [language, setLanguage] = useState("en");
  const navigate = useNavigate();
  const { logout } = useAuth();

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const [invoiceData, setInvoiceData] = useState([]);
  const [archiveData, setArchiveData] = useState(null);
  const [archiveStats, setArchiveStats] = useState({
    numberOfFiles: 0,
    uniquePrefixes: 0,
  });
  const [isDigitalArchiveModalOpen, setDigitalArchiveModalOpen] =
    useState(false);

  useEffect(() => {
    const storedItem = sessionStorage.getItem("trafficDetailsItem");
    if (storedItem) {
      setItem(JSON.parse(storedItem));
      // Not sure this works, but found could be useful
      // sessionStorage.removeItem('trafficDetailsItem');
    }
  }, []);

  // Fetch the invoice data based on reference
  useEffect(() => {
    const fetchInvoiceData = async () => {
      try {
        if (!item.header?.reference) {
          console.warn("No reference available to fetch invoice data");
          return;
        }

        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_API_URL}/api/traffic`,
          {
            params: {
              reference: item.header.reference,
              responseType: "I",
            },
          }
        );

        setInvoiceData(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        console.error("Error fetching invoice data:", error);
        setInvoiceData([]);
      }
    };

    if (item.header?.reference) {
      fetchInvoiceData();
    }
  }, [item.header?.reference]);

  useEffect(() => {
    const pedimento = item.header?.pedimento;
    if (!pedimento) return;

    const { pedimento_entry_point, pedimento_license, pedimento_number } =
      pedimento;
    const url = `https://wisdomconnect-ps-4b0861a5d362.herokuapp.com/api/generalCatalogue/slamdigital/expediente/find/${pedimento_entry_point}/${pedimento_license}/${pedimento_number}`;

    const fetchData = async () => {
      try {
        const response = await fetch(url, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({}),
        });
        if (response.ok) {
          const data = await response.json();
          setArchiveData(data);

          // Calculate statistics
          const numberOfFiles = data.length;
          const prefixSet = new Set(data.map((file) => file.Prefijo));
          const uniquePrefixes = prefixSet.size;

          setArchiveStats({ numberOfFiles, uniquePrefixes });
        } else {
          throw new Error("Failed to fetch data");
        }
      } catch (error) {
        console.error("Error fetching digital archive data:", error);
      }
    };

    fetchData();
  }, [item.header?.pedimento]);

  // Helper function to format date strings
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const FullScreenModal = ({
    isOpen,
    onClose,
    children,
    titleIcon,
    title,
    onExportCsv,
    showExport = false,
  }) => {
    if (!isOpen) return null;

    return (
      <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50">
        <div className="min-h-screen flex items-center justify-center">
          <div className="bg-white p-4 max-w-full max-h-full overflow-auto">
            {/* Modal Title Section */}
            <div className="flex items-center px-3 py-1 bg-blue-900">
              {titleIcon && (
                <FontAwesomeIcon
                  icon={titleIcon}
                  className="text-blue-200 text-lg"
                />
              )}
              <h3 className="font-black text-lg ml-2 text-white">{title}</h3>
              <div className="ml-auto flex items-center">
                {showExport && (
                  <FontAwesomeIcon
                    icon={faFileCsv}
                    className="text-blue-200 text-lg cursor-pointer mr-2"
                    onClick={onExportCsv}
                  />
                )}
                <FontAwesomeIcon
                  icon={faTimes}
                  className="text-blue-200 text-lg cursor-pointer"
                  onClick={onClose}
                />
              </div>
            </div>
            {children}
          </div>
        </div>
      </div>
    );
  };

  const getPieChartData = (invoiceData) => {
    const fractionCount = {}; // Object to hold the count for each fraction number

    invoiceData.forEach((invoice) => {
      invoice.invoice_data.classification_data.forEach((lineItem) => {
        const fraction = lineItem.fraction_number;
        fractionCount[fraction] = (fractionCount[fraction] || 0) + 1;
      });
    });

    // Prepare data for Pie chart
    const labels = Object.keys(fractionCount);
    const data = Object.values(fractionCount);

    return {
      labels,
      datasets: [
        {
          label: "Fraction Number Distribution",
          data,
          backgroundColor: [
            // Define colors for each slice
            "rgba(255, 99, 132, 0.2)",
            "rgba(54, 162, 235, 0.2)",
          ],
          borderColor: ["rgba(255, 99, 132, 1)", "rgba(54, 162, 235, 1)"],
          borderWidth: 1,
        },
      ],
    };
  };

  const calculateIndicators = (invoiceData, billOfLadingData) => {
    const documentSet = new Set();
    let lineNumbersCount = 0;
    const fractionSet = new Set();
    const billOfLadingSet = new Set();

    invoiceData.forEach((invoice) => {
      documentSet.add(invoice.invoice_data.invoice_number);
      invoice.invoice_data.classification_data.forEach((classification) => {
        lineNumbersCount += 1;
        fractionSet.add(classification.fraction_number);
      });
    });

    billOfLadingData.forEach((bol) => {
      billOfLadingSet.add(bol.bill_of_lading);
    });

    return {
      uniqueDocumentsCount: documentSet.size,
      lineNumbersCount: lineNumbersCount,
      uniqueFractionsCount: fractionSet.size,
      uniqueBillOfLadingCount: billOfLadingSet.size,
    };
  };

  const billOfLadingData = item.header?.bill_of_lading_data || [];
  const {
    uniqueDocumentsCount,
    lineNumbersCount,
    uniqueFractionsCount,
    uniqueBillOfLadingCount,
  } = calculateIndicators(invoiceData, billOfLadingData);

  const PieChartCard = ({ invoiceData }) => {
    const chartData = getPieChartData(invoiceData);
    const fractionCount = {};

    invoiceData.forEach((invoice) => {
      invoice.invoice_data.classification_data.forEach((lineItem) => {
        fractionCount[lineItem.fraction_number] =
          (fractionCount[lineItem.fraction_number] || 0) + 1;
      });
    });

    const sortedFractionCount = Object.entries(fractionCount).sort(
      (a, b) => b[1] - a[1]
    );

    return (
      <Card
        icon={faListCheck}
        title="Fraction Distribution"
        details={
          <div className="flex flex-col md:flex-row">
            <div className="w-full md:w-1/2 h-48 md:pr-2">
              <Pie
                data={chartData}
                options={{
                  maintainAspectRatio: true,
                  plugins: {
                    legend: {
                      display: false,
                      position: "right",
                      labels: { fontSize: 10 },
                    },
                  },
                }}
              />
            </div>
            <div className="w-full md:w-1/2 mt-4 md:mt-0 md:pl-2 max-h-48 overflow-auto">
              <table className="min-w-full text-xs">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-2 py-1 text-left">Fraction</th>
                    <th className="px-2 py-1 text-left">Count</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedFractionCount.map(([fraction, count], index) => (
                    <tr
                      key={index}
                      className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}
                    >
                      <td className="px-2 py-1">{fraction}</td>
                      <td className="px-2 py-1">{count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        }
      />
    );
  };

  // Define a Card component for reusability
  // Enhanced Card component for better space utilization and consistent styling
  const Card = ({
    icon,
    title,
    details,
    expandable = false,
    onExpand,
    className = "",
  }) => (
    <div
      className={`bg-white shadow rounded-lg overflow-hidden mb-4 ${className}`}
    >
      <div className="flex justify-between items-center px-3 py-2 bg-blue-900">
        <div className="flex items-center">
          <FontAwesomeIcon icon={icon} className="text-blue-200 text-base" />
          <h3 className="font-bold text-base ml-2 text-white">{title}</h3>
        </div>
        {expandable && (
          <FontAwesomeIcon
            icon={faExpandAlt}
            className="text-blue-200 text-base cursor-pointer"
            onClick={onExpand}
          />
        )}
      </div>
      <div className="p-3">
        {Array.isArray(details) ? (
          <div className="grid grid-cols-2 gap-2">
            {details.map((detail, index) => {
              if (React.isValidElement(detail)) {
                // If it's a React element, span full width
                return (
                  <div key={index} className="col-span-2">
                    {detail}
                  </div>
                );
              } else {
                // Regular detail items
                return (
                  <div key={index} className="flex flex-col">
                    <p className="text-xs text-gray-500 mb-1">{detail.label}</p>
                    <p
                      className="text-sm font-medium truncate"
                      title={detail.value}
                    >
                      {detail.value || "—"}
                    </p>
                  </div>
                );
              }
            })}
          </div>
        ) : (
          // For non-array content
          <div>{details}</div>
        )}
      </div>
    </div>
  );

  const TimelineCard = ({ datesData }) => {
    const [isModalOpen, setIsModalOpen] = useState(false);

    return (
      <>
        <Card
          icon={faClockFour}
          title="Timeline"
          details={<Timeline datesData={datesData} />}
          expandable={true}
          onExpand={() => setIsModalOpen(true)}
        />

        <FullScreenModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          titleIcon={faClockFour}
          title="Timeline Details"
          showExport={false}
        >
          <div className="p-4">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left">Milestone</th>
                  <th className="px-3 py-2 text-left">Date</th>
                </tr>
              </thead>
              <tbody>
                {Object.keys(datesData).map((key, index) => (
                  <tr
                    key={index}
                    className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}
                  >
                    <td className="px-3 py-2">{key.replace(/_/g, " ")}</td>
                    <td className="px-3 py-2">
                      {datesData[key]
                        ? formatDate(datesData[key])
                        : "Not Available"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </FullScreenModal>
      </>
    );
  };

  const DigitalArchiveCard = () => (
    <Card
      icon={faFileCircleCheck}
      title="Digital Archive"
      details={[
        <MiniDashboardDigitalArchive
          numberOfFiles={archiveStats.numberOfFiles}
          uniquePrefixes={archiveStats.uniquePrefixes}
        />,
        <button
          onClick={() => setDigitalArchiveModalOpen(true)}
          className="text-blue-500 hover:text-blue-700 text-sm font-medium mt-2"
        >
          View Files <FontAwesomeIcon icon={faExpandAlt} className="ml-1" />
        </button>,
      ]}
    />
  );

  // InvoiceDetailCard Component
  const InvoiceDetailCard = ({ invoiceData }) => {
    const [isModalOpen, setIsModalOpen] = useState(false);

    // Check if invoiceData is an array and has items
    if (!Array.isArray(invoiceData) || invoiceData.length === 0) {
      return (
        <Card
          icon={faFileInvoice}
          title="Invoice Details"
          details={
            <p className="text-center text-gray-500 py-4">
              No invoice data available.
            </p>
          }
        />
      );
    }

    const exportToCSV = () => {
      let csvContent = "data:text/csv;charset=utf-8,";
      csvContent +=
        "Line Number,Part Number,Description,Fraction,Quantity,Origin,TLC,Prosec,IVA,Payment Method,Value\r\n";

      invoiceData.forEach((invoice) => {
        invoice.invoice_data.classification_data.forEach((row) => {
          let rowContent = `${row.line_number},${
            row.part_number
          },${row.part_description.replace(/,/g, "")},${row.fraction_number},${
            row.quantity
          },${row.country_origin},${row.tlc},${row.prosec},${row.tax_iva},${
            row.iva_payment_method
          },${row.line_value}`;
          csvContent += rowContent + "\r\n";
        });
      });

      const encodedUri = encodeURI(csvContent);
      const link = document.createElement("a");
      link.setAttribute("href", encodedUri);
      link.setAttribute("download", "invoice_data.csv");
      document.body.appendChild(link);
      link.click();
    };

    const invoiceContent = (
      <div className="space-y-4">
        {invoiceData.map((invoice, index) => (
          <div key={index}>
            <div className="flex justify-between items-center mb-2">
              <h4 className="font-semibold text-sm">
                Invoice #{invoice.invoice_data.invoice_number}
              </h4>
              <FontAwesomeIcon
                icon={faFileCsv}
                className="text-blue-500 text-sm cursor-pointer"
                onClick={exportToCSV}
                title="Export to CSV"
              />
            </div>
            <div className="overflow-x-auto max-h-[600px]">
              <table className="min-w-full text-xs">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-2 py-1 text-left">Line</th>
                    <th className="px-2 py-1 text-left">Part</th>
                    <th className="px-2 py-1 text-left">Description</th>
                    <th className="px-2 py-1 text-left">Fraction</th>
                    <th className="px-2 py-1 text-left">Qty</th>
                    <th className="px-2 py-1 text-left">Origin</th>
                    <th className="px-2 py-1 text-left">Value</th>
                  </tr>
                </thead>
                <tbody>
                  {invoice.invoice_data.classification_data?.map(
                    (item, lineIndex) => (
                      <tr
                        key={lineIndex}
                        className={
                          lineIndex % 2 === 0 ? "bg-white" : "bg-gray-50"
                        }
                      >
                        <td className="px-2 py-1">{item.line_number}</td>
                        <td className="px-2 py-1">{item.part_number}</td>
                        <td className="px-2 py-1">
                          <div className="relative group">
                            <span className="truncate inline-block max-w-[120px]">
                              {item.part_description}
                            </span>
                            <span className="absolute left-0 top-full bg-black text-white text-xs rounded py-1 px-2 hidden group-hover:block z-10 w-48">
                              {item.part_description}
                            </span>
                          </div>
                        </td>
                        <td className="px-2 py-1">{item.fraction_number}</td>
                        <td className="px-2 py-1">{item.quantity}</td>
                        <td className="px-2 py-1">{item.country_origin}</td>
                        <td className="px-2 py-1">{item.line_value}</td>
                      </tr>
                    )
                  )}
                </tbody>
              </table>
            </div>
          </div>
        ))}
      </div>
    );

    const modalContent = (
      <div className="p-4">
        {invoiceData.map((invoice, index) => (
          <div key={index} className="mb-6">
            <div className="flex justify-between items-center mb-2">
              <h4 className="font-bold text-lg">
                Invoice #{invoice.invoice_data.invoice_number}
              </h4>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-3 py-2 text-left">
                      Line Number
                    </th>
                    <th scope="col" className="px-3 py-2 text-left">
                      Part Number
                    </th>
                    <th scope="col" className="px-3 py-2 text-left">
                      Description
                    </th>
                    <th scope="col" className="px-3 py-2 text-left">
                      Fraction
                    </th>
                    <th scope="col" className="px-3 py-2 text-left">
                      Quantity
                    </th>
                    <th scope="col" className="px-3 py-2 text-left">
                      Origin
                    </th>
                    <th scope="col" className="px-3 py-2 text-left">
                      TLC
                    </th>
                    <th scope="col" className="px-3 py-2 text-left">
                      Prosec
                    </th>
                    <th scope="col" className="px-3 py-2 text-left">
                      IVA
                    </th>
                    <th scope="col" className="px-3 py-2 text-left">
                      Payment Method
                    </th>
                    <th scope="col" className="px-3 py-2 text-left">
                      Value
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {invoice.invoice_data.classification_data?.map(
                    (item, lineIndex) => (
                      <tr
                        key={lineIndex}
                        className={
                          lineIndex % 2 === 0 ? "bg-white" : "bg-gray-50"
                        }
                      >
                        <td className="px-3 py-2">{item.line_number}</td>
                        <td className="px-3 py-2">{item.part_number}</td>
                        <td className="px-3 py-2">{item.part_description}</td>
                        <td className="px-3 py-2">{item.fraction_number}</td>
                        <td className="px-3 py-2">{item.quantity}</td>
                        <td className="px-3 py-2">{item.country_origin}</td>
                        <td className="px-3 py-2">{item.tlc}</td>
                        <td className="px-3 py-2">{item.prosec}</td>
                        <td className="px-3 py-2">{item.tax_iva}</td>
                        <td className="px-3 py-2">{item.iva_payment_method}</td>
                        <td className="px-3 py-2">{item.line_value}</td>
                      </tr>
                    )
                  )}
                </tbody>
              </table>
            </div>
          </div>
        ))}
      </div>
    );

    return (
      <>
        <Card
          icon={faFileInvoice}
          title="Invoice Details"
          details={invoiceContent}
          expandable={true}
          onExpand={() => setIsModalOpen(true)}
          className="h-full"
        />

        <FullScreenModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onExportCsv={exportToCSV}
          titleIcon={faFileInvoice}
          title="Invoice Details"
          showExport={true}
        >
          {modalContent}
        </FullScreenModal>
      </>
    );
  };

  return (
    <>
      {/* Navbar - Simplified */}
      <div
        className="w-full flex justify-between items-center px-2 md:px-4 py-2 md:py-4"
        style={{ backgroundColor: "#1A2654" }}
      >
        <div className="flex items-center">
          <img src={logo} alt="Company Logo" className="h-16 mr-2" />
        </div>
        <div className="ml-auto flex items-center">
          <h1 className="text-xl font-semibold text-white mr-4">
            Traffic Detail Monitor
          </h1>
          <button
            onClick={handleLogout}
            className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
          >
            {getTranslation(language, "logout")}
          </button>
          <select
            onChange={(e) => setLanguage(e.target.value)}
            value={language}
            className="ml-4 bg-gray-200 border border-gray-400 rounded-md py-1 px-2"
          >
            <option value="en">English</option>
            <option value="es">Español</option>
          </select>
        </div>
      </div>

      <div className="min-h-screen bg-gray-100 p-3">
        {/* Header Section - More compact */}
        <div className="bg-white p-3 rounded-lg shadow mb-4">
          <div className="flex flex-col md:flex-row justify-between items-baseline">
            <div className="flex items-center mb-2 md:mb-0">
              <FontAwesomeIcon
                icon={faCartFlatbed}
                className="text-blue-900 mr-2"
              />
              <h1 className="text-xl font-bold mr-2">
                {item.header?.reference}
              </h1>
              <h2 className="text-base font-medium text-gray-600">
                {item.header?.customer_name}
              </h2>
            </div>
          </div>
          <div className="mt-3">
            <MiniDashboardTrafficDetail
              uniqueDocumentsCount={uniqueDocumentsCount}
              lineNumbersCount={lineNumbersCount}
              uniqueFractionsCount={uniqueFractionsCount}
              uniqueBillOfLadingCount={uniqueBillOfLadingCount}
            />
          </div>
        </div>

        {/* Main Content - 3 columns with better spacing */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {/* Column 1 */}
          <div>
            <Card
              icon={faTruckMoving}
              title="Reference Details"
              details={[
                {
                  label: "Entry Point",
                  value: item.header?.pedimento?.pedimento_entry_point,
                },
                {
                  label: "License",
                  value: item.header?.pedimento?.pedimento_license,
                },
                {
                  label: "Pedimento",
                  value: item.header?.pedimento?.pedimento_number,
                },
                {
                  label: "Operation Type",
                  value:
                    item.header?.operation_type === "1"
                      ? "Import"
                      : item.header?.operation_type === "2"
                      ? "Export"
                      : "Unknown",
                },
                {
                  label: "Priority",
                  value:
                    item.header?.bill_of_lading_data?.bill_of_lading_urgent ===
                    "1"
                      ? "High Priority"
                      : "Normal",
                },
              ]}
            />

            <Card
              icon={faFileInvoiceDollar}
              title="Invoice/Commercial Data"
              details={[
                {
                  label: "BOL",
                  value: [
                    ...new Set(
                      item.header?.bill_of_lading_data?.map(
                        (bol) => bol.bill_of_lading
                      )
                    ),
                  ].join(", "),
                },
                { label: "PO", value: item.header?.purchase_order },
                {
                  label: "Supplier",
                  value:
                    invoiceData.length > 0
                      ? invoiceData[0].invoice_data.supplier
                      : "—",
                },
                {
                  label: "Invoices",
                  value: [
                    ...new Set(
                      invoiceData.map(
                        (invoice) => invoice.invoice_data.invoice_number
                      )
                    ),
                  ].join(", "),
                },
                {
                  label: "Inv. Country",
                  value:
                    invoiceData.length > 0
                      ? invoiceData[0].invoice_data.invoice_country
                      : "—",
                },
                {
                  label: "Cargo Value",
                  value:
                    invoiceData.length > 0
                      ? invoiceData[0].invoice_data.cargo_value
                      : "—",
                },
                {
                  label: "Inv. Value",
                  value:
                    invoiceData.length > 0
                      ? invoiceData[0].invoice_data.invoice_value
                      : "—",
                },
                {
                  label: "Incoterm",
                  value:
                    invoiceData.length > 0
                      ? invoiceData[0].invoice_data.incoterm
                      : "—",
                },
              ]}
            />

            <Card
              icon={faPlaneArrival}
              title="Traffic Data"
              details={[
                {
                  label: "Status",
                  value: item.header?.detail?.traffic_data?.status,
                },
                {
                  label: "Regime",
                  value: item.header?.detail?.traffic_data?.regime,
                },
                {
                  label: "MX Carrier",
                  value: item.header?.detail?.traffic_data?.mexican_carrier,
                },
                {
                  label: "US Carrier",
                  value: item.header?.detail?.traffic_data?.us_carrier,
                },
                {
                  label: "MX Trailer",
                  value:
                    item.header?.detail?.traffic_data
                      ?.mexican_carrier_trailer_number,
                },
                {
                  label: "US Trailer",
                  value:
                    item.header?.detail?.traffic_data
                      ?.us_carrier_trailer_number,
                },
                {
                  label: "Observations",
                  value: item.header?.detail?.traffic_data?.observations,
                },
                {
                  label: "Traffic Obs.",
                  value:
                    item.header?.detail?.traffic_data?.traffic_observations,
                },
                {
                  label: "Annotations",
                  value: item.header?.detail?.traffic_data?.annotations,
                },
                { label: "Total Packages", value: item.header?.total_packages },
                { label: "Weight (lbs)", value: item.header?.weight },
              ]}
            />

            <DigitalArchiveCard />
          </div>

          {/* Column 2 */}
          <div>
            <InvoiceDetailCard invoiceData={invoiceData} />
          </div>

          {/* Column 3 */}
          <div>
            <TimelineCard datesData={item.header?.detail?.dates_data || {}} />
            <PieChartCard invoiceData={invoiceData} />
          </div>
        </div>
      </div>

      {/* Modals */}
      {isDigitalArchiveModalOpen && (
        <DigitalFilesModal
          isOpen={isDigitalArchiveModalOpen}
          onClose={() => setDigitalArchiveModalOpen(false)}
          files={archiveData}
        />
      )}
    </>
  );
};

export default TrafficDetails;
